// Full-page civil takeoff workspace — /app/takeoffs/:tid
// PDF viewer + scaled measurement tools + layers + rate build-up + quote export.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Check, Loader2, MousePointer2, Hand, Ruler, Hexagon, Hash, Box, Waves,
  Crosshair, Upload, ChevronLeft, ChevronRight, FileText, Trash2, Layers as LayersIcon,
  Spline, Minus, Square, Sprout, Mountain, Landmark, Trophy, Sparkles, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import TakeoffCanvas from "@/components/TakeoffCanvas";
import { InlineErrorBoundary } from "@/components/ErrorBoundary";
import {
  LayersPanel, MeasurementsPanel, SummaryPanel, ScaleDialog, ExportDialog,
} from "@/components/TakeoffParts";
import {
  EstimateTab, RisksTab, RfisTab, NotesTab, ProgrammeTab, SuppliersTab, WinJobDialog,
} from "@/components/TakeoffTabs";
import TakeoffAIReader from "@/components/TakeoffAIReader";
import TakeoffAIPanel, { DISCIPLINE_COLORS } from "@/components/TakeoffAIPanel";
import TakeoffAIReview from "@/components/TakeoffAIReview";
import { MEASURE_TYPES, computeQty, computeDerived } from "@/lib/takeoffGeo";

const TOOLS = [
  { key: "select", icon: MousePointer2, label: "Select", hint: "Click a measurement to edit it" },
  { key: "pan", icon: Hand, label: "Pan", hint: "Drag to move around (or scroll to zoom)" },
  { key: "calibrate", icon: Crosshair, label: "Calibrate", hint: "Click both ends of a known length" },
  { key: "length", icon: Ruler, label: "Length (L)", hint: "Click points along a run — fences, marking" },
  { key: "area", icon: Hexagon, label: "Area (A)", hint: "Click around a boundary" },
  { key: "count", icon: Hash, label: "Count (C)", hint: "Click each item — sumps, trees, signs" },
  { key: "volume", icon: Box, label: "Volume", hint: "Outline the area, then set a depth" },
  { key: "trench", icon: Waves, label: "Trench (T)", hint: "Trace the trench — excavation, bedding, backfill & spoil auto-calculate" },
  { key: "pipe", icon: Spline, label: "Pipe Run (P)", hint: "Trace the pipe — set DN and material in the panel" },
  { key: "kerb", icon: Minus, label: "Kerb & Channel", hint: "Trace the kerb line" },
  { key: "pavement", icon: Square, label: "Pavement", hint: "Outline the area, then define the layer build-up" },
  { key: "topsoil", icon: Sprout, label: "Topsoil Strip", hint: "Outline the strip area, set the depth" },
  { key: "cutfill", icon: Mountain, label: "Cut / Fill", hint: "Outline the zone, set the average depth" },
  { key: "structure", icon: Landmark, label: "Structure", hint: "Click each manhole / catchpit / chamber" },
];

const HOTKEYS = { v: "select", l: "length", a: "area", c: "count", t: "trench", p: "pipe" };

const TOOL_DEFAULTS = {
  trench: { meta: { bedding_mm: 150, bulking: 1.2, pipe_displacement: false, pipe_dn_mm: 300, backfill: "AP40" }, depth: 1.2, width: 0.6 },
  pipe: { meta: { dn: "DN300", material: "RCRRJ" } },
  pavement: { meta: { layers: [{ name: "AP65", thickness_mm: 250, density_t_m3: 2.2 }, { name: "AP40", thickness_mm: 150, density_t_m3: 2.2 }] } },
  topsoil: { depth: 0.15 },
  cutfill: { depth: 0.5, meta: { mode: "cut" } },
  structure: { meta: { template: "Manhole 1050Ø" } },
  volume: { depth: 0.5 },
};

const WORKSPACE_TABS = [
  ["takeoff", "Takeoff"], ["estimate", "Estimate"], ["suppliers", "Suppliers"],
  ["programme", "Programme"], ["risks", "Risks"], ["rfis", "RFIs"], ["notes", "Notes"],
];

const STATUS_STYLE = {
  draft: "bg-zinc-200 text-zinc-700 dark:bg-white/10 dark:text-zinc-300",
  in_progress: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  complete: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  exported: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
  won: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
};

export default function TakeoffWorkspace() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved");
  const [tool, setTool] = useState("pan");

  // keyboard shortcuts (V/L/A/C/T/P, Esc)
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT" || e.metaKey || e.ctrlKey) return;
      const k = e.key.toLowerCase();
      if (k === "escape") { setTool("select"); return; }
      if (HOTKEYS[k]) setTool(HOTKEYS[k]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const [activeDrawingId, setActiveDrawingId] = useState(null);
  const [page, setPage] = useState(1);
  const [canvasNonce, setCanvasNonce] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [scaleOpen, setScaleOpen] = useState(false);
  const [calibration, setCalibration] = useState(null); // {dist} in doc units
  const [exportOpen, setExportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("takeoff");
  const [winOpen, setWinOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [focusRegion, setFocusRegion] = useState(null);
  const [aiMeasure, setAiMeasure] = useState(null); // finding being measured on plan
  const skipSave = useRef(true);
  const saveTimer = useRef(null);
  const fileRef = useRef(null);

  // ---------- load ----------
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/takeoffs/${tid}`);
        skipSave.current = true;
        setT(data);
        setActiveDrawingId(data.drawings?.[0]?.id || null);
        setActiveLayerId(data.layers?.[0]?.id || null);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load takeoff");
        navigate("/app/takeoffs");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid]);

  // ---------- autosave ----------
  const patch = useCallback((changes) => setT((prev) => ({ ...prev, ...changes })), []);
  useEffect(() => {
    if (!t?.id) return;
    if (skipSave.current) { skipSave.current = false; return; }
    setSaveState("dirty");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        await api.patch(`/takeoffs/${t.id}`, {
          name: t.name, status: t.status, notes: t.notes,
          client_id: t.client_id, client_name: t.client_name,
          layers: t.layers, measurements: t.measurements, drawings: t.drawings,
          risks: t.risks || [], rfis: t.rfis || [], qnotes: t.qnotes || [],
          estimate: t.estimate || {}, checklist: t.checklist || {},
          programme: t.programme || [], versions: t.versions || [],
          enquiries: t.enquiries || [],
          ai_findings: t.ai_findings || [], ai_issues: t.ai_issues || [],
        });
        setSaveState("saved");
      } catch { setSaveState("dirty"); }
    }, 1200);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // ---------- derived ----------
  const drawing = useMemo(() => (t?.drawings || []).find((d) => d.id === activeDrawingId) || null, [t, activeDrawingId]);
  const scale = drawing?.scales?.[String(page)] || null;
  const mPerUnit = scale?.m_per_unit || null;
  const measurements = t?.measurements || [];
  const layers = t?.layers || [];

  // ---------- measurement ops ----------
  const updateMeasurement = (id, changes) => {
    patch({
      measurements: measurements.map((m) => {
        if (m.id !== id) return m;
        const next = { ...m, ...changes };
        if ((next.points || []).length > 0 && ("depth" in changes || "width" in changes || "meta" in changes)) {
          const d = (t.drawings || []).find((x) => x.id === next.drawing_id);
          const mpu = d?.scales?.[String(next.page || 1)]?.m_per_unit || null;
          next.qty = round2(computeQty(next, mpu));
          next.derived = computeDerived(next, mpu);
        }
        if ("derived" in changes) next.derived = changes.derived;
        return next;
      }),
    });
  };
  const deleteMeasurement = (id) => {
    patch({ measurements: measurements.filter((m) => m.id !== id) });
    if (selectedId === id) setSelectedId(null);
  };

  const onShapeComplete = (shapeTool, points) => {
    if (shapeTool === "calibrate") {
      const dist = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
      setCalibration({ dist });
      setScaleOpen(true);
      return;
    }
    if (!mPerUnit && !["count", "structure"].includes(shapeTool)) {
      toast.error("Set the drawing scale first");
      setScaleOpen(true);
      return;
    }
    const cfg = MEASURE_TYPES[shapeTool];
    const defaults = TOOL_DEFAULTS[shapeTool] || {};
    const n = measurements.filter((m) => m.type === shapeTool).length + 1;
    const m = {
      id: crypto.randomUUID(),
      drawing_id: drawing.id,
      page,
      layer_id: activeLayerId,
      type: shapeTool,
      name: shapeTool === "structure" ? `${defaults.meta?.template || "Structure"} ${n}`
        : shapeTool === "pipe" ? `${defaults.meta?.dn || "Pipe"} run ${n}`
        : `${cfg.label} ${n}`,
      points,
      unit: cfg.unit,
      depth: defaults.depth ?? (["volume", "trench"].includes(shapeTool) ? 0.5 : null),
      width: defaults.width ?? (shapeTool === "trench" ? 0.6 : null),
      meta: defaults.meta ? JSON.parse(JSON.stringify(defaults.meta)) : undefined,
      waste_pct: 0,
      rate: { unit_rate: null, labour_cost: null, material_cost: null, plant_cost: null },
      notes: "",
    };
    m.qty = round2(computeQty(m, mPerUnit));
    m.derived = computeDerived(m, mPerUnit);
    // AI "measure on plan" — link this measurement back to the pending finding
    if (aiMeasure && shapeTool === aiMeasure.type) {
      m.name = aiMeasure.name;
      m.source = "ai";
      m.ai = { fid: aiMeasure.fid, confidence: aiMeasure.confidence, evidence: aiMeasure.evidence || null };
      if (aiMeasure.depth_m != null && m.depth == null) m.depth = aiMeasure.depth_m;
      patch({
        measurements: [...measurements, m],
        ai_findings: (t.ai_findings || []).map((f) => f.fid === aiMeasure.fid
          ? { ...f, qty: m.qty, status: "accepted", built: true, measurement_id: m.id } : f),
      });
      setSelectedId(m.id);
      setAiMeasure(null);
      setTool("select");
      setAiPanelOpen(true);
      toast.success(`${round2(m.qty)} ${m.unit} captured for "${m.name}" — ✓ ready`);
      return;
    }
    patch({ measurements: [...measurements, m] });
    setSelectedId(m.id);
    if (["volume", "trench", "topsoil", "cutfill"].includes(shapeTool)) toast.info("Check the depth in the panel to get m³");
  };

  const applyScale = (s, allPages) => {
    const pages = allPages
      ? Array.from({ length: drawing.page_count || 1 }, (_, i) => String(i + 1))
      : [String(page)];
    const scales = { ...(drawing.scales || {}) };
    pages.forEach((p) => { scales[p] = s; });
    const drawings = t.drawings.map((d) => (d.id === drawing.id ? { ...d, scales } : d));
    const ms = measurements.map((m) => {
      if (!(m.points || []).length || m.drawing_id !== drawing.id || !pages.includes(String(m.page || 1))) return m;
      const next = { ...m, qty: round2(computeQty(m, s.m_per_unit)) };
      next.derived = computeDerived(next, s.m_per_unit);
      return next;
    });
    patch({ drawings, measurements: ms });
    setCalibration(null);
    toast.success(`Scale set to ${s.label}`);
  };

  // ---------- AI takeoff assistant ----------
  const findings = t?.ai_findings || [];
  const aiIssues = t?.ai_issues || [];
  const updateFinding = (fid, changes) =>
    setT((prev) => ({ ...prev, ai_findings: (prev.ai_findings || []).map((f) => (f.fid === fid ? { ...f, ...changes } : f)) }));
  const updateIssue = (id, changes) =>
    setT((prev) => ({ ...prev, ai_issues: (prev.ai_issues || []).map((i) => (i.id === id ? { ...i, ...changes } : i)) }));

  const onAiFindings = ({ findings: newF, issues: newI }) => {
    // re-scan: replace pending/rejected findings on the scanned pages, keep accepted/built ones
    const keyOf = (f) => `${f.drawing_id}:${f.page}`;
    const scanned = new Set(newF.map(keyOf));
    const kept = findings.filter((f) => !scanned.has(keyOf(f)) || f.built || ["accepted", "edited"].includes(f.status));
    const keptIssues = aiIssues.filter((i) => !scanned.has(`${i.drawing_id}:${i.page}`) || i.status === "dismissed");
    patch({ ai_findings: [...kept, ...newF], ai_issues: [...keptIssues, ...newI] });
    setAiOpen(false);
    setAiPanelOpen(true);
    toast.success(`${newF.length} item${newF.length === 1 ? "" : "s"} found — review them in the assistant`);
  };

  const locateFinding = (f, all = false) => {
    setTab("takeoff");
    if (f.drawing_id !== activeDrawingId) setActiveDrawingId(f.drawing_id);
    if ((f.page || 1) !== page) setPage(f.page || 1);
    const rects = all && f.locations?.length ? f.locations : [f.bbox].filter(Boolean);
    if (rects.length) setFocusRegion({ rects, key: Date.now() });
  };

  const measureFinding = (f) => {
    setTab("takeoff");
    if (f.drawing_id !== activeDrawingId) setActiveDrawingId(f.drawing_id);
    if ((f.page || 1) !== page) setPage(f.page || 1);
    if (f.bbox) setFocusRegion({ rects: [f.bbox], key: Date.now() });
    setAiMeasure(f);
    setTool(MEASURE_TYPES[f.type] ? f.type : "length");
    toast.info(`Trace "${f.name}" on the plan — the quantity flows straight back to the AI item`);
  };

  const buildFromFindings = (builds) => {
    const layersNext = [...(t.layers || [])];
    const layerFor = (disc) => {
      const name = disc || "Other";
      let l = layersNext.find((x) => (x.name || "").toLowerCase() === name.toLowerCase());
      if (!l) {
        l = { id: crypto.randomUUID(), name, color: DISCIPLINE_COLORS[name] || "#71717a", visible: true };
        layersNext.push(l);
      }
      return l;
    };
    const adds = [];
    const builtFids = new Set();
    builds.forEach(({ f, unitRate, rateDesc, trenchItems }) => {
      const layer = layerFor(f.discipline);
      builtFids.add(f.fid);
      const meta = f.type === "pipe" ? { dn: f.size || "DN?", material: f.material || "" }
        : f.type === "structure" ? { template: f.size || "Structure" } : undefined;
      const base = {
        drawing_id: f.drawing_id, page: f.page, layer_id: layer.id, points: [], width: null,
        waste_pct: 0, source: "ai",
      };
      adds.push({
        ...base, id: crypto.randomUUID(), type: f.type, name: f.name, qty: f.qty ?? 0, unit: f.unit,
        depth: f.depth_m ?? null, meta,
        rate: { unit_rate: unitRate ?? null, labour_cost: null, material_cost: null, plant_cost: null },
        notes: f.notes || "",
        ai: { fid: f.fid, confidence: f.confidence, evidence: f.evidence || null, rate_match: rateDesc || null, drawing_name: f.drawing_name },
      });
      (trenchItems || []).forEach((ti) => adds.push({
        ...base, id: crypto.randomUUID(), type: "volume", name: ti.name, qty: ti.qty, unit: "m³", depth: null,
        rate: { unit_rate: null, labour_cost: null, material_cost: null, plant_cost: null },
        notes: ti.notes || "",
        ai: { fid: f.fid, confidence: f.confidence, derived: true, evidence: f.evidence || null, drawing_name: f.drawing_name },
      }));
    });
    patch({
      layers: layersNext,
      measurements: [...measurements, ...adds],
      ai_findings: findings.map((f) => builtFids.has(f.fid)
        ? { ...f, status: f.status === "pending" ? "accepted" : f.status, built: true } : f),
    });
    setAiReviewOpen(false);
    setTab("takeoff");
    toast.success(`${adds.length} item${adds.length === 1 ? "" : "s"} added to the takeoff — ✨ AI assisted`);
  };

  const quickAddAccepted = () => {
    const eligible = findings.filter((f) => ["accepted", "edited"].includes(f.status) && !f.built && f.qty != null);
    buildFromFindings(eligible.map((f) => ({ f })));
  };

  // ---------- drawings ----------
  const uploadDrawing = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/takeoffs/${t.id}/drawings`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      skipSave.current = true;
      setT((prev) => ({ ...prev, drawings: [...(prev.drawings || []), data], status: prev.status === "draft" ? "in_progress" : prev.status }));
      setActiveDrawingId(data.id);
      setPage(1);
      toast.success(`${data.name} added (${data.page_count} page${data.page_count > 1 ? "s" : ""})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  const deleteDrawing = async (d) => {
    const count = measurements.filter((m) => m.drawing_id === d.id).length;
    if (!window.confirm(`Remove "${d.name}"${count ? ` and its ${count} measurement(s)` : ""}?`)) return;
    try {
      await api.delete(`/takeoffs/${t.id}/drawings/${d.id}`);
      skipSave.current = true;
      setT((prev) => ({
        ...prev,
        drawings: prev.drawings.filter((x) => x.id !== d.id),
        measurements: prev.measurements.filter((m) => m.drawing_id !== d.id),
      }));
      if (activeDrawingId === d.id) { setActiveDrawingId(t.drawings.find((x) => x.id !== d.id)?.id || null); setPage(1); }
    } catch { toast.error("Delete failed"); }
  };

  if (loading || !t) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-7 h-7 animate-spin text-[#FF5F15]" /></div>;
  }

  const status = t.status || "draft";
  const activeTool = TOOLS.find((x) => x.key === tool);

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-zinc-100 dark:bg-[#0B0F14]" data-testid="takeoff-workspace">
      {/* ===== header ===== */}
      <div className="shrink-0 border-b border-zinc-200 dark:border-white/10 bg-white/95 dark:bg-[#0B0F14]/95 backdrop-blur px-3 lg:px-5 py-2 flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate("/app/takeoffs")} className="text-zinc-400 hover:text-[#FF5F15]" data-testid="takeoff-back">
          <ArrowLeft className="w-4.5 h-4.5" />
        </button>
        <input value={t.name || ""} onChange={(e) => patch({ name: e.target.value })}
          className="font-display font-black uppercase tracking-tight text-lg bg-transparent outline-none border-b border-transparent focus:border-[#FF5F15] min-w-0 w-48 lg:w-72"
          data-testid="takeoff-name-input" />
        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[status] || STATUS_STYLE.draft}`} data-testid="takeoff-status-badge">{status.replace("_", " ")}</span>
        <span className="hidden md:inline text-[11px] text-zinc-400" data-testid="takeoff-autosave-state">
          {saveState === "saved" ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Saved</span>
            : saveState === "saving" ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>
            : "Unsaved changes"}
        </span>
        <div className="flex-1" />
        {drawing && (
          <button onClick={() => setScaleOpen(true)}
            className={`px-2.5 py-1 text-[11px] font-bold font-mono border ${scale ? "border-emerald-500/40 text-emerald-500" : "border-amber-500/60 text-amber-500 animate-pulse"}`}
            data-testid="takeoff-scale-badge">
            {scale ? `Scale ${scale.label}` : "⚠ Not calibrated"}
          </button>
        )}
        {t.exported_quote_id && (
          <Button size="sm" variant="outline" onClick={() => navigate(`/app/quotes/${t.exported_quote_id}`)}
            className="h-8 rounded-sm text-[11px] font-bold uppercase tracking-wider" data-testid="takeoff-open-quote">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Open Quote
          </Button>
        )}
        {t.status === "won" || t.won_job_id ? (
          <Button size="sm" variant="outline" onClick={() => navigate(`/app/jobs/${t.won_job_id || t.job_id}`)}
            className="h-8 rounded-sm text-[11px] font-bold uppercase tracking-wider border-emerald-500/50 text-emerald-500" data-testid="takeoff-open-won-job">
            <Trophy className="w-3.5 h-3.5 mr-1.5" /> Job Won
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setWinOpen(true)} disabled={!measurements.length}
            className="h-8 rounded-sm text-[11px] font-bold uppercase tracking-wider" data-testid="takeoff-win-job-btn">
            <Trophy className="w-3.5 h-3.5 mr-1.5 text-[#FF5F15]" /> Win Job
          </Button>
        )}
        <Button size="sm" onClick={() => setExportOpen(true)} disabled={!measurements.length}
          className="h-8 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="takeoff-header-export">
          Export to Quote
        </Button>
      </div>

      {/* ===== workspace tabs ===== */}
      <div className="shrink-0 flex items-center px-3 lg:px-5 border-b border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]" data-testid="takeoff-tab-bar">
        {WORKSPACE_TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === k ? "border-[#FF5F15] text-[#FF5F15]" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
            data-testid={`takeoff-tab-${k}`}>
            {label}
            {k === "risks" && (t.risks || []).length > 0 && <span className="ml-1 text-[9px] font-mono text-zinc-400">{t.risks.length}</span>}
            {k === "rfis" && (t.rfis || []).some((r) => r.status !== "answered") && <span className="ml-1 w-1.5 h-1.5 inline-block rounded-full bg-amber-500" />}
          </button>
        ))}
      </div>
      {tab !== "takeoff" && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          {tab === "estimate" && <EstimateTab t={t} patch={patch} />}
          {tab === "suppliers" && <SuppliersTab t={t} patch={patch} />}
          {tab === "programme" && <ProgrammeTab t={t} patch={patch} />}
          {tab === "risks" && <RisksTab t={t} patch={patch} />}
          {tab === "rfis" && <RfisTab t={t} patch={patch} />}
          {tab === "notes" && <NotesTab t={t} patch={patch} />}
        </div>
      )}
      {tab === "takeoff" && (<>
      {/* ===== drawing tabs ===== */}
      <div className="shrink-0 flex items-center gap-1 px-3 lg:px-5 py-1.5 border-b border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.02] overflow-x-auto">
        {(t.drawings || []).map((d) => (
          <div key={d.id} className={`group flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold cursor-pointer whitespace-nowrap border ${d.id === activeDrawingId ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/5" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
            onClick={() => { setActiveDrawingId(d.id); setPage(1); }} data-testid={`takeoff-drawing-tab-${d.id}`}>
            <FileText className="w-3 h-3" /> {d.name}
            <button onClick={(e) => { e.stopPropagation(); deleteDrawing(d); }} className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] border border-dashed border-zinc-300 dark:border-white/15 hover:border-[#FF5F15] whitespace-nowrap"
          data-testid="takeoff-upload-drawing">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Add Drawing
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
          onChange={(e) => { uploadDrawing(e.target.files?.[0]); e.target.value = ""; }} />
        {drawing && (
          <button onClick={() => (findings.length ? setAiPanelOpen((v) => !v) : setAiOpen(true))}
            className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#FF5F15] border border-[#FF5F15]/50 hover:bg-[#FF5F15]/10 whitespace-nowrap"
            data-testid="takeoff-ai-read-btn">
            <Sparkles className="w-3 h-3" /> AI Plan Reader
            {findings.filter((f) => f.status !== "rejected").length > 0 && (
              <span className="font-mono text-[10px] bg-[#FF5F15] text-white px-1">{findings.filter((f) => f.status !== "rejected").length}</span>
            )}
          </button>
        )}
        <div className="flex-1" />
        {drawing && drawing.page_count > 1 && (
          <div className="flex items-center gap-1 text-[11px] font-mono text-zinc-500" data-testid="takeoff-page-selector">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="p-1 hover:text-[#FF5F15] disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5" /></button>
            Page {page} / {drawing.page_count}
            <button onClick={() => setPage((p) => Math.min(drawing.page_count, p + 1))} disabled={page >= drawing.page_count} className="p-1 hover:text-[#FF5F15] disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      {/* ===== body: toolbar | canvas | panel ===== */}
      <div className="flex-1 min-h-0 flex">
        {/* toolbar */}
        <div className="shrink-0 w-12 flex flex-col items-center gap-0.5 py-2 border-r border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]">
          {TOOLS.map(({ key, icon: Icon, label }) => (
            <button key={key} onClick={() => setTool(key)} title={label}
              className={`relative group w-9 h-9 flex items-center justify-center ${tool === key ? "bg-[#FF5F15] text-white" : "text-zinc-500 hover:text-[#FF5F15] hover:bg-[#FF5F15]/10"}`}
              data-testid={`takeoff-tool-${key}`}>
              <Icon className="w-4 h-4" />
              <span className="absolute left-11 z-50 hidden group-hover:block px-2 py-1 bg-[#0B0F14] border border-white/10 text-[10px] font-bold uppercase tracking-wider text-zinc-200 whitespace-nowrap">{label}</span>
            </button>
          ))}
        </div>

        {/* canvas */}
        <div className="flex-1 min-w-0 flex flex-col relative">
          {drawing ? (
            <>
              <InlineErrorBoundary
                resetKey={`${drawing.id}-${page}-${canvasNonce}`}
                fallback={
                  <div className="flex-1 min-h-0 flex items-center justify-center bg-zinc-200 dark:bg-[#06090D]" data-testid="takeoff-canvas-error">
                    <div className="text-center max-w-sm px-6">
                      <AlertTriangle className="w-10 h-10 text-[#FF5F15] mx-auto mb-3" />
                      <div className="text-sm font-bold uppercase tracking-wider text-zinc-800 dark:text-zinc-200">This plan couldn't be displayed</div>
                      <p className="text-xs text-zinc-500 mt-1.5">The drawing may be very large or an unusual format. Your measurements are safe.</p>
                      <button
                        onClick={() => setCanvasNonce((n) => n + 1)}
                        className="mt-4 px-4 py-2 bg-[#FF5F15] text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-[#E65100]"
                        data-testid="takeoff-canvas-retry"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                }
              >
                <TakeoffCanvas key={`${drawing.id}-${page}-${canvasNonce}`} drawing={drawing} page={page} tool={tool}
                  layers={layers} measurements={measurements} selectedId={selectedId}
                  onSelect={setSelectedId} onShapeComplete={onShapeComplete} mPerUnit={mPerUnit}
                  focusRegion={focusRegion} />
              </InlineErrorBoundary>
              {aiMeasure && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 bg-[#FF5F15] text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-2 shadow-lg" data-testid="ai-measure-banner">
                  <Sparkles className="w-3.5 h-3.5" /> Measuring: {aiMeasure.name}
                  <button onClick={() => { setAiMeasure(null); setTool("select"); }} className="underline">cancel</button>
                </div>
              )}
              <div className="absolute bottom-3 left-3 z-10 px-2.5 py-1 bg-[#0B0F14]/85 border border-white/10 text-[10px] text-zinc-400 backdrop-blur max-w-[60%]" data-testid="takeoff-tool-hint">
                <b className="text-[#FF5F15] uppercase">{activeTool?.label}</b> — {activeTool?.hint}
              </div>
            </>
          ) : (
            <button onClick={() => fileRef.current?.click()}
              className="flex-1 m-6 border-2 border-dashed border-zinc-300 dark:border-white/15 hover:border-[#FF5F15] flex flex-col items-center justify-center gap-3 text-zinc-500 hover:text-[#FF5F15]"
              data-testid="takeoff-empty-upload">
              <Upload className="w-10 h-10" />
              <span className="text-sm font-bold uppercase tracking-widest">Upload a drawing to start measuring</span>
              <span className="text-xs text-zinc-500">PDF plan sets (multi-page supported), PNG or JPG · up to 40MB</span>
            </button>
          )}
        </div>

        {/* right panel */}
        <div className="shrink-0 w-[340px] hidden lg:flex flex-col gap-3 p-3 overflow-y-auto border-l border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-[#0B0F14]">
          <SummaryPanel layers={layers} measurements={measurements} onExport={() => setExportOpen(true)} exportedQuote={t.exported_quote_id} />
          <MeasurementsPanel layers={layers} measurements={measurements} selectedId={selectedId}
            setSelectedId={setSelectedId} updateMeasurement={updateMeasurement}
            deleteMeasurement={deleteMeasurement} drawings={t.drawings || []} />
          <LayersPanel layers={layers} measurements={measurements} activeLayerId={activeLayerId}
            setActiveLayerId={setActiveLayerId} onChange={(ls) => patch({ layers: ls })} />
        </div>
      </div>

      {/* mobile notice */}
      <div className="lg:hidden shrink-0 px-3 py-2 border-t border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] text-[11px] text-zinc-500 flex items-center gap-2">
        <LayersIcon className="w-3.5 h-3.5 text-[#FF5F15]" /> Takeoff measuring works best on a desktop screen.
      </div>
      </>)}

      <TakeoffAIReader open={aiOpen} onClose={() => setAiOpen(false)} takeoffId={t.id}
        drawing={drawing} currentPage={page} onFindings={onAiFindings} />
      <TakeoffAIPanel open={aiPanelOpen} onClose={() => setAiPanelOpen(false)}
        findings={findings} issues={aiIssues}
        updateFinding={updateFinding} updateIssue={updateIssue}
        onLocate={locateFinding} onMeasure={(f) => { setAiPanelOpen(true); measureFinding(f); }}
        onRescan={() => setAiOpen(true)}
        onBuild={() => setAiReviewOpen(true)} onQuickAdd={quickAddAccepted} />
      <TakeoffAIReview open={aiReviewOpen} onClose={() => setAiReviewOpen(false)}
        takeoffId={t.id} findings={findings} onBuild={buildFromFindings} />
      <WinJobDialog open={winOpen} onClose={() => setWinOpen(false)} t={t}
        onWon={(d) => {
          setWinOpen(false);
          skipSave.current = true;
          clearTimeout(saveTimer.current);
          setT((x) => ({ ...x, status: "won", won_job_id: d.job_id, job_id: d.job_id }));
        }} />
      <ScaleDialog open={scaleOpen} onClose={() => { setScaleOpen(false); setCalibration(null); }}
        calibration={calibration} isPdf={drawing?.kind === "pdf"} currentScale={scale}
        hasPages={(drawing?.page_count || 1) > 1} onApply={applyScale} />
      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} takeoffId={t.id}
        measurementCount={measurements.length}
        onDone={(res) => {
          setExportOpen(false);
          skipSave.current = true;
          setT((prev) => ({ ...prev, exported_quote_id: res.quote_id, status: "exported" }));
          toast.success(res.mode === "created" ? `Quote ${res.quote_number} created` : `Lines added to ${res.quote_number}`, {
            action: { label: "Open quote", onClick: () => navigate(`/app/quotes/${res.quote_id}`) },
          });
        }} />
    </div>
  );
}

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
